import { todayIST } from "@templeos/shared";
import { useToast } from "@templeos/ui";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, ApiError } from "../lib/api.js";
import { cartTotal, CartError, parseCart, reconcileCart, toApiCart, toSaleLines, type Cart } from "../lib/cart.js";
import { db } from "../lib/db.js";
import { postDisplay } from "../lib/display.js";
import { buildPayments, changeDue, PaymentError, upiLink, type PayChoice } from "../lib/payments.js";
import { printJobs, printProblem, saleJobs } from "../lib/print.js";
import { applyResult, chainLink, enqueueSale, isRefusal, randomSuffix, terminalId } from "../lib/queue.js";
import { billProblem } from "../lib/rules.js";
import { within } from "../lib/sync.js";
import type { Payer, Payment, PosItem, QueuedSale, Ticket } from "../lib/types.js";
import { engine, local, useSession } from "../session.js";

/**
 * The bill being built, mirrored to localStorage on every change so a reload or crash never loses it. The key is per
 * counter and cashier, so the next cashier never inherits a bill. A restored bill is shape-checked, and it follows
 * the counter configuration (`items`, null until loaded): stale lines are dropped and prices updated.
 */
export function usePosCart(storageKey: string, items: readonly PosItem[] | null): readonly [Cart, (next: Cart) => void] {
  const [cart, setState] = useState<Cart>(() => parseCart(local.get(storageKey)));
  const setCart = useCallback((next: Cart) => {
    setState(next);
    local.set(storageKey, next.length ? JSON.stringify(next) : null);
  }, [storageKey]);
  useEffect(() => {
    if (!items) return;
    const next = reconcileCart(cart, items, todayIST());
    if (next !== cart) setCart(next);
  }, [items, cart, setCart]);
  return [cart, setCart] as const;
}

export function usePayment() {
  const [choice, setChoice] = useState<PayChoice>("cash");
  const [cashPart, setCashPart] = useState<number | null>(null);
  const [tendered, setTendered] = useState<number | null>(null);
  const [upiRef, setUpiRef] = useState(() => `UPI${randomSuffix(8)}`);
  const [formKey, setFormKey] = useState(0);
  const reset = useCallback(() => {
    setChoice("cash");
    setCashPart(null);
    setTendered(null);
    setUpiRef(`UPI${randomSuffix(8)}`);
    setFormKey((k) => k + 1); // remount the amount inputs empty
  }, []);
  /** Fixed denomination button: the devotee handed over this note. */
  const quickTender = useCallback((paise: number) => {
    setTendered(paise);
    setFormKey((k) => k + 1); // remount so the input shows the new amount
  }, []);
  return { choice, setChoice, cashPart, setCashPart, tendered, setTendered, quickTender, upiRef, formKey, reset };
}

/** Keep the customer-facing screen showing the current bill and the UPI QR; back to welcome when the screen closes. */
export function useDisplaySync(cart: Cart, upi: { link: string; amount: number } | null): void {
  const { slip } = useSession();
  useEffect(() => {
    if (!cart.length) {
      postDisplay({ type: "idle", templeName: slip.templeName });
      return;
    }
    postDisplay({ type: "cart", templeName: slip.templeName, lines: cart.map((l) => ({ name: l.name, qty: l.qty, amount: l.unitPrice * l.qty })), total: cartTotal(cart), upi });
  }, [cart, upi, slip.templeName]);
  useEffect(() => () => postDisplay({ type: "idle", templeName: slip.templeName }), [slip.templeName]);
}

interface SaleResponse { receiptId: string; number: string; total: number; publicToken: string; tickets: Ticket[]; duplicate: boolean }
type PushResult = { result: "synced" | "offline" } | { result: "rejected"; message: string };

/** How long a bill may wait for the live server answer before its slip prints anyway (SOW: under 2 s per devotee). */
const LIVE_BUDGET_MS = 1_200;
const OFFLINE: PushResult = { result: "offline" };

const cleanPayer = (p?: Payer): Payer | undefined => {
  const out = Object.fromEntries(Object.entries(p ?? {}).map(([k, v]) => [k, typeof v === "string" ? v.trim() : v]).filter(([, v]) => v)) as Payer;
  return Object.keys(out).length ? out : undefined;
};

/**
 * Send one sale live. Only a final refusal (validation, stock, quota, tax rules) rejects it; a busy server, a missing
 * shift, device binding and every other failure leave it queued for background sync.
 */
async function pushSale(sale: QueuedSale): Promise<PushResult> {
  try {
    const r = await api.post<SaleResponse>("/api/counters/sales", { clientRef: sale.clientRef, cart: sale.cart, payments: sale.payments, payer: sale.payer,
      chain: chainLink(sale, await terminalId(db)) });
    await applyResult(db, { clientRef: sale.clientRef, status: r.duplicate ? "duplicate" : "ok", receiptId: r.receiptId, number: r.number, publicToken: r.publicToken, tickets: r.tickets });
    return { result: "synced" };
  } catch (e) {
    if (e instanceof ApiError && [400, 409, 422].includes(e.status) && isRefusal(e.code)) {
      await applyResult(db, { clientRef: sale.clientRef, status: "error", error: { code: e.code, message: e.message } });
      return { result: "rejected", message: e.message };
    }
    return OFFLINE;
  }
}

export interface CheckoutInput { cart: Cart; payments: Payment[]; payer?: Payer; change?: number | null; onQueued?: () => void }

/**
 * Single-click checkout: save to the local queue first (the sub-300 ms part), then give the live server a short
 * budget, then print. If the server is slow, busy with a background upload or unreachable, the slip prints against
 * the sale reference and the upload finishes in the background.
 */
export function useCheckout(): (input: CheckoutInput) => Promise<QueuedSale | null> {
  const s = useSession();
  const toast = useToast();
  return useCallback(async (input: CheckoutInput) => {
    if (!s.me?.counter || !s.shift) {
      toast("Open a shift before billing.", "danger");
      return null;
    }
    const total = cartTotal(input.cart);
    const t0 = performance.now();
    const sale = await enqueueSale(db, {
      counterCode: s.me.counter.code, cashier: s.me.username, shiftId: s.shift.shift.id, cart: toApiCart(input.cart), lines: toSaleLines(input.cart),
      payments: input.payments, payer: cleanPayer(input.payer), total,
    }, new Date(), { getItem: local.get, setItem: local.set });
    input.onQueued?.(); // the bill is committed: a reload from here on must not offer the same cart again
    const localMs = Math.round(performance.now() - t0);
    const roll = `roll #${String(sale.seq).padStart(5, "0")}`;

    let pushed = OFFLINE;
    if (s.reachable && !engine.state.running) {
      const live = engine.exclusive(() => pushSale(sale));
      pushed = await within(live, LIVE_BUDGET_MS, OFFLINE);
      if (pushed === OFFLINE) {
        void live.then((late) => {
          if (late.result === "rejected") toast(`Bill ${roll} was refused by the server after printing: ${late.message} Take the slips back and return the money.`, "danger");
        }, (e: unknown) => toast(e instanceof Error ? e.message : "The bill's server answer could not be recorded.", "danger"));
      }
    }
    await engine.refreshCount();
    if (pushed.result === "synced" && engine.state.pending) void engine.kick(); // the server is back: send the backlog
    if (pushed.result === "offline") engine.ensureScheduled();
    const saved = (await db.sales.get(sale.clientRef)) ?? sale;
    if (pushed.result === "rejected") {
      toast(`Bill not accepted: ${pushed.message} Return the money; nothing was printed.`, "danger");
      return saved;
    }
    postDisplay({ type: "paid", templeName: s.slip.templeName, total, change: input.change ?? null, number: saved.number ?? null });
    const printed = await printJobs(s.config?.printerUrl ?? null, saleJobs(saved, s.slip));
    const problem = printProblem(printed);
    const label = saved.number ?? `Saved offline, ${roll}`;
    if (problem) toast(`${label} saved. ${problem}: reprint the missing slips from History${saved.number ? "" : " once the bill has synced"}.`, "danger");
    else toast(`${label} · saved in ${localMs} ms`, pushed.result === "synced" ? "success" : "neutral");
    return saved;
  }, [s, toast]);
}

/**
 * Everything a billing screen needs: the mirrored cart, payment choice, devotee details, the UPI QR for the amount
 * due on UPI, and a guarded submit (one click, no double submission) that checks every rule the server would refuse
 * the bill for after the devotee has paid: quotas and stock (`validate`), PAN, mobile and the s.269ST cash limit.
 */
export function useBill(storageKey: string, items: readonly PosItem[] | null, validate: (cart: Cart) => string | null) {
  const s = useSession();
  const toast = useToast();
  const checkout = useCheckout();
  const [cart, setCart] = usePosCart(storageKey, items);
  const pay = usePayment();
  const [payer, setPayer] = useState<Payer>({});
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false); // a second F5 before the re-render must not bill twice
  const [lastSale, setLastSale] = useState<QueuedSale | null>(null);
  const total = cartTotal(cart);
  const donation = cart.filter((l) => l.kind === "donation").reduce((sum, l) => sum + l.unitPrice * l.qty, 0);
  const upiAmount = total === 0 ? 0 : pay.choice === "upi" ? total : pay.choice === "split" && pay.cashPart !== null && pay.cashPart > 0 && pay.cashPart < total ? total - pay.cashPart : 0;
  const vpa = s.config?.upiVpa ?? null;
  const { upi, upiError } = useMemo(() => {
    if (!upiAmount) return { upi: null, upiError: null };
    if (!vpa) return { upi: null, upiError: "UPI ID not configured for this counter; use the counter's printed UPI standee." };
    try {
      return { upi: { link: upiLink({ vpa, payee: s.slip.templeName, amount: upiAmount, ref: pay.upiRef }), amount: upiAmount }, upiError: null };
    } catch (e) {
      return { upi: null, upiError: (e as Error).message };
    }
  }, [upiAmount, vpa, s.slip.templeName, pay.upiRef]);
  useDisplaySync(cart, upi);

  const submit = useCallback(async () => {
    if (inFlight.current) return;
    if (!cart.length) { toast("The bill is empty.", "danger"); return; }
    const problem = validate(cart);
    if (problem) { toast(problem, "danger"); return; }
    let payments: Payment[];
    let change: number | null = null;
    try {
      payments = buildPayments(total, pay.choice, pay.cashPart, pay.upiRef);
      const cash = payments.filter((p) => p.mode === "cash").reduce((a, p) => a + p.amount, 0);
      const tax = billProblem({ payer, donation, cash }, s.limits);
      if (tax) { toast(tax, "danger"); return; }
      if (cash) change = changeDue(cash, pay.tendered);
    } catch (e) {
      if (e instanceof PaymentError || e instanceof CartError) { toast(e.message, "danger"); return; }
      throw e;
    }
    inFlight.current = true;
    setBusy(true);
    try {
      const sale = await checkout({ cart, payments, payer, change, onQueued: () => local.set(storageKey, null) });
      if (sale && sale.status !== "rejected") {
        setLastSale(sale);
        setCart([]);
        setPayer({});
        pay.reset();
      }
    } catch (e) {
      toast(e instanceof Error ? e.message : "The bill could not be saved.", "danger");
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }, [cart, validate, total, pay, payer, donation, s.limits, checkout, setCart, storageKey, toast]);

  return { cart, setCart, pay, payer, setPayer, total, donation, upi, upiError, submit, busy, lastSale };
}
export type Bill = ReturnType<typeof useBill>;
